// App.tsx (Corrected)
import { useEffect, lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams, Outlet } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminRoute } from "@/components/admin/AdminRoute";
import { InfluencerRoute } from "@/components/InfluencerRoute";
import { OAuthCallback } from "@/components/auth/OAuthCallback";
import ThemeToggle from "@/components/ThemeToggle";
import FigmaCallback from "@/pages/FigmaCallback";
import Connectors from "@/pages/Connectors";
import UniversalAIKeyPage from "./pages/UniversalAIKeyPage";
import ReferralsPage from "./pages/ReferralsPage";
import ExpertHelpPage from "./pages/ExpertHelpPage";
import ExpertRequestPage from "./pages/ExpertRequestPage";
import { Navbar } from "@/components/Navbar";

// Eagerly loaded — critical for initial paint & unauth landing/auth flow.
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import NotFound from "./pages/NotFound";
import SEO from "@/components/SEO";
import { StripeProvider } from '@/components/stripe/StripeProvider';
import { GitHubProvider } from "./contexts/GithubContext";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import { AppShell } from "@/components/shell/AppShell";
import { WorkflowAttentionProvider } from "@/contexts/WorkflowAttentionContext";
import { trackBillingReturnCandidate } from "@/utils/billingReturn";
import { captureAttributionFromUrl } from "@/utils/attribution";

// Lazy-loaded routes — split into separate chunks, fetched on demand.
const ProjectEditor = lazy(() => import("./pages/ProjectEditor"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const CheckEmail = lazy(() => import("./pages/CheckEmail"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const Home = lazy(() => import("./pages/Home"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const SecurityPage = lazy(() => import("./pages/SecurityPage"));
const InfluencerDashboard = lazy(() => import("./pages/InfluencerDashboard"));
const BillingPlans = lazy(() => import("./components/BillingPlans"));
const BillingPayment = lazy(() => import("./components/BillingPayment"));
const BillingSuccess = lazy(() => import("./components/BillingSuccess"));
const BillingManage = lazy(() => import("./components/BillingManage"));
const ManageProjectCollaboration = lazy(() =>
  import("./components/collaboration/ManageProjectCollaboration").then((m) => ({ default: m.ManageProjectCollaboration }))
);
const PublishedAppsPage = lazy(() => import("./pages/PublishedAppPage"));
const TeamSettings = lazy(() =>
  import("./pages/TeamSettings").then((m) => ({ default: m.TeamSettings }))
);
const SettingsShell = lazy(() =>
  import("./components/settings/SettingsShell").then((m) => ({ default: m.SettingsShell }))
);

// Admin (heavy — rarely used by typical users)
const AdminLayout = lazy(() =>
  import("./components/admin/AdminLayout").then((m) => ({ default: m.AdminLayout }))
);
const AdminDashboard = lazy(() =>
  import("./pages/admin/AdminDashboard").then((m) => ({ default: m.AdminDashboard }))
);
const AdminUsers = lazy(() =>
  import("./pages/admin/AdminUsers").then((m) => ({ default: m.AdminUsers }))
);
const AdminPayments = lazy(() =>
  import("./pages/admin/AdminPayments").then((m) => ({ default: m.AdminPayments }))
);
const AdminProjects = lazy(() =>
  import("./pages/admin/AdminProjects").then((m) => ({ default: m.AdminProjects }))
);
const AdminProjectCreditUsage = lazy(() =>
  import("./pages/admin/AdminProjectCreditUsage").then((m) => ({ default: m.AdminProjectCreditUsage }))
);
const AdminUserCreditUsage = lazy(() =>
  import("./pages/admin/AdminUserCreditUsage").then((m) => ({ default: m.AdminUserCreditUsage }))
);
const AdminRoles = lazy(() =>
  import("./pages/admin/AdminRoles").then((m) => ({ default: m.AdminRoles }))
);
const AdminBilling = lazy(() =>
  import("./pages/admin/AdminBilling").then((m) => ({ default: m.AdminBilling }))
);
const AdminEngagements = lazy(() =>
  import("./pages/admin/AdminEngagements").then((m) => ({ default: m.AdminEngagements }))
);
const AdminSupportTickets = lazy(() =>
  import("./pages/admin/AdminSupportTickets").then((m) => ({ default: m.AdminSupportTickets }))
);
const AdminSupportTicketDetailPage = lazy(() =>
  import("./pages/admin/AdminSupportTicketDetailPage").then((m) => ({ default: m.AdminSupportTicketDetailPage }))
);
const AdminVouchers = lazy(() =>
  import("./pages/admin/AdminVouchers").then((m) => ({ default: m.AdminVouchers }))
);
const EngagementChatView = lazy(() =>
  import("./components/engagement/EngagementChatView").then((m) => ({ default: m.EngagementChatView }))
);
const EngagementBoard = lazy(() =>
  import("./components/engagement/EngagementBoard").then((m) => ({ default: m.EngagementBoard }))
);
const EngagementWorkLogView = lazy(() =>
  import("./components/engagement/EngagementWorkLogView").then((m) => ({ default: m.EngagementWorkLogView }))
);
const DeveloperDashboard = lazy(() =>
  import("./pages/developer/DeveloperDashboard").then((m) => ({ default: m.DeveloperDashboard }))
);
const EngagementLayout = lazy(() =>
  import("./components/engagement/EngagementLayout").then((m) => ({ default: m.EngagementLayout }))
);
const AdminUsageAnalytics = lazy(() =>
  import("./pages/admin/AdminUsageAnalytics").then((m) => ({ default: m.AdminUsageAnalytics }))
);
const AdminCosts = lazy(() =>
  import("./pages/admin/AdminCosts").then((m) => ({ default: m.AdminCosts }))
);
const AdminAuditLogs = lazy(() =>
  import("./pages/admin/AdminAuditLogs").then((m) => ({ default: m.AdminAuditLogs }))
);
const AdminReferrals = lazy(() =>
  import("./pages/admin/AdminReferrals").then((m) => ({ default: m.AdminReferrals }))
);
const AdminAttributions = lazy(() =>
  import("./pages/admin/AdminAttributions").then((m) => ({ default: m.AdminAttributions }))
);
const AdminInvites = lazy(() =>
  import("./pages/admin/AdminInvites").then((m) => ({ default: m.AdminInvites }))
);
const AdminInfluencers = lazy(() =>
  import("./pages/admin/AdminInfluencers").then((m) => ({ default: m.AdminInfluencers }))
);
const AdminPublishedApps = lazy(() =>
  import("./pages/admin/AdminPublishedApps").then((m) => ({ default: m.AdminPublishedApps }))
);

// Public marketing/help pages (prerendered at build time — SSG content already in HTML,
// so the JS chunk only hydrates after first paint).
const Pricing = lazy(() => import("./pages/Pricing"));
const Help = lazy(() => import("./pages/Help"));
const ProductionGuarantee = lazy(() => import("./pages/ProductionGuarantee"));
const MyTickets = lazy(() => import("./pages/MyTickets"));
const HowCreditsWork = lazy(() => import("./pages/help/articles/HowCreditsWork"));
const CustomDomain = lazy(() => import("./pages/help/articles/CustomDomain"));
const JoyloArchitect = lazy(() => import("./pages/help/articles/JoyloArchitect"));
const CancelDowngrade = lazy(() => import("./pages/help/articles/CancelDowngrade"));
const AppSleeping = lazy(() => import("./pages/help/articles/AppSleeping"));
const AiModels = lazy(() => import("./pages/help/articles/AiModels"));

const AllProjects = lazy(() => import("./pages/AllProjects"));
const GitHubCallbackHandler = lazy(() => import("./components/Github/GithubCallbackHandler"));

// An empty aria-hidden div read as a frozen page: every lazy route showed a blank screen for as
// long as its chunk took to load, which users reported as the app hanging after a click.
// Pages opened from a project (account menu, Engage an expert) carry `returnTo` so the top bar can offer a way back
const ShellLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const returnTo = (location.state as { returnTo?: { pathname: string; search?: string } } | null)?.returnTo;
  return (
    <AppShell
      onBack={returnTo ? () => navigate(returnTo.pathname + (returnTo.search || "")) : undefined}
      backLabel={returnTo?.pathname.startsWith("/projects/") ? "Back to project" : "Back"}
    >
      <Outlet />
    </AppShell>
  );
};

const RouteFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center" role="status" aria-label="Loading">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

const queryClient = new QueryClient();




const AUTH_ROUTES = ['/login', '/signup', '/forgot-password', '/reset-password', '/verify-email', '/check-email', '/accept-invite'];

const AppContent = () => {
  const location = useLocation();
  const hideNavbarOnRoutes = ['/login', '/signup'];
  const isAdminRoute = location.pathname.startsWith('/admin');
  // The Figma callback path is fixed by what is registered on the Figma app, so it does not follow
  // the /auth/callback/ convention and needs naming here to render without the app chrome
  const isAuthRoute = AUTH_ROUTES.includes(location.pathname)
    || location.pathname.startsWith('/auth/callback/')
    || location.pathname === '/api/auth/figma/callback';
  const shouldShowNavbar = !hideNavbarOnRoutes.includes(location.pathname) && !isAdminRoute;
  const backgroundLocation = (location.state as any)?.background;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    trackBillingReturnCandidate(location.pathname, location.search);
  }, [location.pathname, location.search]);

  // First-touch attribution must be captured wherever the visitor lands — most paid
  // traffic arrives on a marketing page, not /signup — so this runs app-wide. It's a
  // no-op once the 30-day cookie exists.
  useEffect(() => {
    captureAttributionFromUrl();
  }, []);

  // Shared account/settings pages — declared once, rendered by whichever tree
  // matches: the main tree when there's no background location (standalone
  // full page), or the overlay tree below when there is one (rendered on top
  // of the still-mounted background page).
  const settingsRoutes = (
    <Route element={<ProtectedRoute><SettingsShell /></ProtectedRoute>}>
      <Route path="profile" element={<ProfilePage />} />
      <Route path="security" element={<SecurityPage />} />
      <Route path="billing/manage" element={<BillingManage />} />
      <Route path="published" element={<PublishedAppsPage />} />
      <Route path="my-tickets" element={<MyTickets />} />
      <Route path="influencer" element={<InfluencerRoute><InfluencerDashboard /></InfluencerRoute>} />
      <Route path="workspace/settings" element={<TeamSettings />} />
    </Route>
  );

  return (
    <div className="App">
      <StripeProvider>
      <WorkflowAttentionProvider>
        {/* Noindex for auth and OAuth callback routes */}
        {isAuthRoute && <SEO robots="noindex, follow" />}

        {/* Conditionally render navbar - hide on auth pages and admin pages */}
        {shouldShowNavbar && <Navbar />}

        <Suspense fallback={<RouteFallback />}>
        <Routes location={backgroundLocation || location}>

          {/* Public routes - NO navbar */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/check-email" element={<CheckEmail />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/production-guarantee" element={<ProductionGuarantee />} />
          <Route path="/help" element={<Help />} />
          <Route path="/help/articles/how-credits-work" element={<HowCreditsWork />} />
          <Route path="/help/articles/custom-domain" element={<CustomDomain />} />
          <Route path="/help/articles/joylo-architect" element={<JoyloArchitect />} />
          <Route path="/help/articles/cancel-downgrade" element={<CancelDowngrade />} />
          <Route path="/help/articles/app-sleeping" element={<AppSleeping />} />
          <Route path="/help/articles/ai-models" element={<AiModels />} />

          {/* OAuth callback routes */}
          <Route path="/api/auth/figma/callback" element={<FigmaCallback />} />
          <Route path="/auth/callback/google" element={<OAuthCallback />} />
          <Route path="/auth/callback/github" element={<OAuthCallback />} />
          <Route path="/auth/callback/facebook" element={<OAuthCallback />} />
          <Route path="/auth/callback/apple" element={<OAuthCallback />} />
          <Route path="/auth/callback/microsoft" element={<OAuthCallback />} />
          {/* <Route path="/auth/callback/:provider" element={<OAuthCallback />} /> */}

          {/* Admin routes - NO navbar, has its own layout */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminRoute permission="users:read"><AdminUsers /></AdminRoute>} />
            <Route path="users/:userId" element={<AdminRoute permission="users:read"><AdminUserCreditUsage /></AdminRoute>} />
            <Route path="projects" element={<AdminRoute permission="projects:read"><AdminProjects /></AdminRoute>} />
            <Route path="projects/:projectId" element={<AdminRoute permission="projects:read"><AdminProjectCreditUsage /></AdminRoute>} />
            <Route path="published-apps" element={<AdminRoute permission="projects:read"><AdminPublishedApps /></AdminRoute>} />
            <Route path="roles" element={<AdminRoute permission="rbac:read"><AdminRoles /></AdminRoute>} />
            <Route path="billing" element={<AdminRoute permission="billing:read"><AdminBilling /></AdminRoute>} />
            <Route path="payments" element={<AdminRoute permission="billing:read"><AdminPayments /></AdminRoute>} />
            <Route path="engagements" element={<AdminRoute permission="engagements:read"><AdminEngagements /></AdminRoute>} />
            <Route path="support-tickets" element={<AdminRoute permission="support:read"><AdminSupportTickets /></AdminRoute>} />
            <Route path="support-tickets/:ticketId" element={<AdminRoute permission="support:read"><AdminSupportTicketDetailPage /></AdminRoute>} />
            <Route path="vouchers" element={<AdminRoute permission="vouchers:read"><AdminVouchers /></AdminRoute>} />
            <Route path="audit-logs" element={<AdminRoute permission="audit:read"><AdminAuditLogs /></AdminRoute>} />
            <Route path="costs" element={<AdminRoute permission="costs:read"><AdminCosts /></AdminRoute>} />
            <Route path="referrals" element={<AdminRoute permission="referrals:read"><AdminReferrals /></AdminRoute>} />
            <Route path="attributions" element={<AdminRoute permission="attributions:read"><AdminAttributions /></AdminRoute>} />
            <Route path="invites" element={<AdminRoute permission="invites:read"><AdminInvites /></AdminRoute>} />
            <Route path="influencers" element={<AdminRoute permission="influencers:read"><AdminInfluencers /></AdminRoute>} />
          </Route>

          {/* Workspace pages rendered inside the sidebar app shell */}
          <Route element={<ProtectedRoute><ShellLayout /></ProtectedRoute>}>
            <Route path="/~" element={<Home />} />
            <Route path="/projects/all" element={<AllProjects />} />
            <Route path="/connectors" element={<Connectors />} />
            <Route path="/universal-ai-key" element={<UniversalAIKeyPage />} />
            <Route path="/referrals" element={<ReferralsPage />} />
            <Route path="/expert" element={<ExpertHelpPage />} />
            <Route path="/expert/request" element={<ExpertRequestPage />} />
            <Route path="/billing" element={<Outlet />}>
              <Route path="plans" element={<BillingPlans />} />
              <Route path="payment" element={<BillingPayment />} />
              <Route path="success" element={<BillingSuccess />} />
              <Route index element={<Navigate to="plans" replace />} />
            </Route>
            <Route path="/projects/:type/:projectId/engagement" element={<EngagementLayout />}>
              <Route path="chat" element={<EngagementChatView />} />
              <Route path="board" element={<EngagementBoard />} />
              <Route path="logs" element={<EngagementWorkLogView />} />
            </Route>
          </Route>

          {/* Project routes - support both patterns for backwards compatibility */}
          <Route
            path="/projects/:projectId"
            element={
              <ProtectedRoute>
                <ProjectEditor />
              </ProtectedRoute>
            }
          />


          <Route
            path="/projects/:type/:projectId"
            element={
              <ProtectedRoute>
                <ProjectEditor />
              </ProtectedRoute>
            }
          />

          {/* Developer Specific Routes */}
          <Route
            path="/developer/dashboard"
            element={
              <ProtectedRoute>
                <DeveloperDashboard />
              </ProtectedRoute>
            }
          />

          {/* Collaboration Management Route */}
          <Route
            path="/projects/:projectId/collaborate"
            element={
              <ProtectedRoute>
                <ManageProjectCollaboration />
              </ProtectedRoute>
            }
          />

          {/* Add GitHub callback route */}
          <Route
            path="/github/callback"
            element={<GitHubCallbackHandler />}
          />


          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AppShell>
                  <AdminUsageAnalytics />
                </AppShell>
              </ProtectedRoute>}
          />

          {/* Additional protected routes */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <div className="min-h-screen flex items-center justify-center bg-background">
                  <div className="text-center">
                    <h1 className="text-2xl font-bold mb-4 gradient-text">Account Settings</h1>
                    <p className="text-muted-foreground">Settings panel coming soon...</p>
                    <button
                      onClick={() => window.history.back()}
                      className="mt-4 px-4 py-2 bg-gradient-to-r from-[#9b87f5] to-[#8B5CF6] text-white rounded-lg hover:opacity-90 transition-opacity"
                    >
                      Go Back
                    </button>
                  </div>
                </div>
              </ProtectedRoute>
            }
          />

          {settingsRoutes}

          {/* 404 Route */}
          <Route path="*" element={<NotFound />} />
        </Routes >
        </Suspense>

        {/* Settings overlay — only mounted while a background location is set, rendering the
            settings route on top of the still-mounted page underneath (see backgroundLocation above) */}
        {backgroundLocation && (
          <Suspense fallback={<RouteFallback />}>
            <Routes location={location}>
              {settingsRoutes}
            </Routes>
          </Suspense>
        )}
      </WorkflowAttentionProvider>
      </StripeProvider>
    </div >
  );
};

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="joylo-theme">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider>
            <GitHubProvider>
              <WorkspaceProvider>
                <BrowserRouter>
                  <AppContent />
                  <Toaster />
                  <Sonner />
                  <ThemeToggle />
                </BrowserRouter>
              </WorkspaceProvider>
            </GitHubProvider>
          </AuthProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;